import { Radar, MailCheck, Send, X, UserCheck, Lock, Star, Archive, Undo2, Inbox, MailPlus, BookOpen } from "lucide-react";


import { Brand } from "./Brand";
import { Button } from "./ui/button";

const TabNav = ({ tab, setTab, teamCount, isAdmin, limited }) => (
  <nav className="flex shrink-0 gap-1 bg-slate-100 rounded-full p-1" data-testid="main-tabs">
    {[["leads", "Leads"], ...(limited ? [] : [["big", "BIG"]]), ...(isAdmin ? [["team", "Team"]] : [])].map(([k, label]) => (
      <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
        className={`px-2.5 sm:px-4 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${k === "big" ? "font-extrabold" : "font-medium"} ${k === "big" ? (tab === k ? "bg-green-300 text-green-950 shadow-sm ring-2 ring-green-500" : "bg-green-200 text-green-900 hover:bg-green-300") : tab === k ? "bg-white text-blue-700 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}>
        {label}
      </button>
    ))}
  </nav>
);

const ScanningIndicator = () => (
  <span className="hidden xl:flex items-center gap-2 text-xs text-blue-700 font-medium whitespace-nowrap" data-testid="scan-running-indicator">
    <span className="relative flex h-2 w-2"><span className="animate-ping absolute h-full w-full rounded-full bg-blue-500 opacity-75" /><span className="relative h-2 w-2 rounded-full bg-blue-600" /></span>
    Scanning the web…
  </span>
);

export const DashboardHeader = ({ tab, setTab, teamCount, running, onScan, user, onLogout }) => {
  const demo = user?.role === "demo";
  return (
  <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-lg border-b border-slate-200">
    <div className="max-w-[1600px] mx-auto px-3 sm:px-6 h-16 flex items-center justify-between gap-2">
      <div className="flex items-center gap-2 sm:gap-3 lg:gap-8 min-w-0">
        <div className="shrink-0"><Brand /></div>
        <TabNav tab={tab} setTab={setTab} teamCount={teamCount} isAdmin={user?.role === "admin"} limited={user?.limited} />
      </div>
      <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
        {running && <ScanningIndicator />}
        {!demo && !user?.limited && (
          <Button onClick={onScan} className="rounded-full bg-blue-700 hover:bg-blue-800 h-10 px-3 xl:px-5" data-testid="scan-now-button">
            <Radar className={`h-4 w-4 xl:mr-2 ${running ? "animate-spin xl:animate-none" : ""}`} /><span className="hidden xl:inline">Scan now</span>
          </Button>
        )}
        {demo && <span className="text-[10px] font-mono-label px-2 py-0.5 rounded-full bg-orange-500 text-white" data-testid="demo-badge">Demo</span>}
        <span className="hidden xl:inline text-sm text-slate-500 whitespace-nowrap" data-testid="current-user-name">{user?.name}</span>
        {user?.role === "admin" && <span className="hidden xl:inline text-[10px] font-mono-label px-2 py-0.5 rounded-full bg-blue-700 text-white" data-testid="admin-badge">Admin</span>}
        <Button onClick={onLogout} variant="outline" title="Logout" className="rounded-full h-9 px-3 sm:px-4 border-red-600 text-red-600 hover:bg-red-600 hover:text-white font-bold" data-testid="logout-button">
          Logout
        </Button>
      </div>
    </div>
  </header>
  );
};

export const DemoBanner = ({ other }) => (
  <div className="rounded-2xl bg-slate-900 text-white px-5 py-4 flex items-center gap-3 fade-up" data-testid="demo-banner">
    <Lock className="h-5 w-5 text-orange-400 shrink-0" />
    <p className="text-sm">{other ? <>You're viewing the demo. Deep dive near me finds up to <b>10 restaurants within 50 miles</b> of you, shown in full.</>
      : <>You're viewing the demo. You can see <b>one full lead per state</b>; the rest stay locked.</>}</p>
  </div>
);

export const OwnerEmailChip = ({ active, onToggle, testid }) => (
  <button onClick={onToggle} aria-pressed={active} data-testid={testid}
    className={`h-10 px-5 rounded-full text-sm font-semibold border flex items-center gap-2 transition-colors ${active ? "bg-blue-700 border-blue-700 text-white" : "bg-white border-slate-200 text-slate-800 hover:border-blue-300"}`}>
    <MailCheck className="h-4 w-4" />Has owner email
  </button>
);

export const NotEmailedChip = ({ active, onToggle }) => (
  <button onClick={onToggle} aria-pressed={active} data-testid="not-emailed-filter"
    className={`h-10 px-5 rounded-full text-sm font-semibold border flex items-center gap-2 transition-colors ${active ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-slate-200 text-slate-800 hover:border-emerald-400"}`}>
    <MailCheck className="h-4 w-4" />Not emailed yet
  </button>
);

export const OwnerGotEmailChip = ({ active, onToggle }) => (
  <button onClick={onToggle} aria-pressed={active} data-testid="owner-got-email-filter"
    className={`h-10 px-5 rounded-full text-sm font-semibold border flex items-center gap-2 transition-colors ${active ? "bg-emerald-800 border-emerald-800 text-white" : "bg-white border-slate-200 text-slate-800 hover:border-emerald-400"}`}>
    <MailPlus className="h-4 w-4" />Owner got email
  </button>
);

export const SentChip = ({ active, onToggle, isAdmin }) => (
  <button onClick={onToggle} aria-pressed={active} data-testid="sent-filter"
    className={`h-10 px-5 rounded-full text-sm font-semibold border flex items-center gap-2 transition-colors ${active ? "bg-amber-300 border-amber-400 text-amber-950" : "bg-white border-slate-200 text-slate-800 hover:border-amber-300"}`}>
    <Send className="h-4 w-4" />{isAdmin ? "Sent to reps" : "Sent to me"}
  </button>
);

export const MyLeadsToggle = ({ active, due, onToggle }) => (
  <div className="flex flex-wrap items-center gap-2.5">
    <button onClick={onToggle} data-testid="my-leads-button"
      className={`h-10 px-5 rounded-full text-sm font-semibold border flex items-center gap-2 transition-colors ${active ? "bg-blue-700 border-blue-700 text-white" : "bg-white border-slate-200 text-slate-800 hover:border-blue-300"}`}>
      <UserCheck className="h-4 w-4" />My leads
      {due > 0 && <span className={`text-[11px] px-1.5 rounded-full ${active ? "bg-white text-blue-700" : "bg-red-600 text-white"}`} data-testid="my-due-count">{due} due</span>}
    </button>
    {active && <span className="text-xs text-slate-500" data-testid="my-leads-hint">Assigned to you · follow-ups soonest first</span>}
  </div>
);

const BarBtn = ({ onClick, icon: Icon, children, testid }) => (
  <Button onClick={onClick} variant="ghost" className="rounded-full h-9 px-3 text-slate-100 hover:text-white hover:bg-slate-800" data-testid={testid}>
    <Icon className="h-4 w-4 mr-1.5" />{children}
  </Button>
);

export const PageTitle = ({ title, subtitle }) => (
  <div className="fade-up">
    <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">{title}</h1>
    <p className="text-sm text-slate-500 mt-1">{subtitle}</p>
  </div>
);
